import { Button, Text } from "@aurem/ui";
import { ExternalLink } from "lucide-react";
import { useCallback } from "react";
import { DevToolbarCard } from "../components/DevToolbarCard";

const isLocal = process.env.REACT_APP_ENVIRONMENT === "local";

type LinkCardProps = {
  label: string;
  description: string;
  url: string;
  testId: string;
};

const LinkCard = ({ label, description, url, testId }: LinkCardProps) => {
  const openInNewTab = useCallback((targetUrl: string) => {
    window.open(targetUrl, "_blank", "noopener,noreferrer");
  }, []);

  return (
    <DevToolbarCard title={label} testId={testId}>
      <div className="flex items-center justify-between gap-2">
        <Text size="xs" className="text-gray500 flex-1">
          {description}
        </Text>
        <Button
          variant="ghost"
          color="neutral"
          size="xs"
          onClick={() => openInNewTab(url)}
          startIcon={<ExternalLink size={14} />}
          data-e2e-id={`${testId}-open`}
        >
          Open
        </Button>
      </div>
    </DevToolbarCard>
  );
};

const linkUrl = (key: string, fallback: string): string =>
  process.env[key] || fallback;

const LOCAL_LINKS = [
  {
    label: "Temporal UI",
    description: "Workflow monitoring dashboard",
    url: linkUrl("TEMPORAL_UI_URL", "https://temporal.aurem.localhost"),
    testId: "devtoolbar-link-temporal",
  },
  {
    label: "Mailcatcher",
    description: "Email testing interface",
    url: linkUrl("MAILCATCHER_URL", "https://mail.aurem.localhost"),
    testId: "devtoolbar-link-mailcatcher",
  },
  {
    label: "Traefik Dashboard",
    description: "Routing dashboard",
    url: linkUrl("TRAEFIK_URL", "https://traefik.aurem.localhost"),
    testId: "devtoolbar-link-traefik",
  },
  {
    label: "MinIO Console",
    description: "S3 storage management",
    url: linkUrl("MINIO_URL", "https://minio.aurem.localhost"),
    testId: "devtoolbar-link-minio",
  },
  {
    label: "Templates Preview",
    description: "Email/SMS/Document preview",
    url: linkUrl("TEMPLATES_PREVIEW_URL", "https://preview.aurem.localhost"),
    testId: "devtoolbar-link-templates",
  },
  {
    label: "Storybook",
    description: "UI kit component library",
    url: linkUrl("STORYBOOK_URL", "https://storybook.aurem.localhost"),
    testId: "devtoolbar-link-storybook",
  },
];

export const LinksTab = () => {
  if (!isLocal) {
    return (
      <div
        className="p-4 h-full flex items-center justify-center"
        data-e2e-id="devtoolbar-links-tab"
      >
        <Text size="sm" className="text-gray500">
          Local service links are only available in the local environment.
        </Text>
      </div>
    );
  }

  return (
    <div
      className="px-4 py-3 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-3"
      data-e2e-id="devtoolbar-links-tab"
    >
      {LOCAL_LINKS.map((link) => (
        <LinkCard key={link.url} {...link} />
      ))}
    </div>
  );
};
